import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { fmtUSD } from '../../utils/formatters';
import { adminApi } from '../../services/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const CreditManagement = () => {
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.get('/admin/credit/requests');
      setRequests(res.data?.requests || []);
    } catch (e) {
      dispatch(showToast({ message: 'Failed to load credit requests', type: 'error' }));
    } finally { setIsLoading(false); }
  };

  const handleApprove = async (id) => {
    setProcessingIds(prev => [...prev, id]);
    try {
      await adminApi.post(`/admin/credit/${id}/approve`);
      dispatch(showToast({ message: 'Credit request approved', type: 'success' }));
      fetchRequests();
    } catch (e) { dispatch(showToast({ message: 'Failed to approve', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(pid => pid !== id)); }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) { dispatch(showToast({ message: 'Please provide a reason', type: 'error' })); return; }
    setProcessingIds(prev => [...prev, id]);
    try {
      await adminApi.post(`/admin/credit/${id}/reject`, { reason: rejectReason });
      dispatch(showToast({ message: 'Credit request rejected', type: 'error' }));
      setRejectReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (e) { dispatch(showToast({ message: 'Failed to reject', type: 'error' })); }
    finally { setProcessingIds(prev => prev.filter(pid => pid !== id)); }
  };

  const getDocUrl = (filename) => {
    if (!filename) return null;
    return `${API_BASE}/uploads/credit/${filename}`;
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#047857]">APPROVED</span>;
    if (status === 'rejected') return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#dc2626] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b91c1c]">REJECTED</span>;
    return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#d97706] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] border-[#b45309]">PENDING</span>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">CREDIT REQUESTS</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">Manage user credit applications</p>
        </div>
        <button onClick={fetchRequests} 
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]">
          <i className="fas fa-sync-alt mr-1.5" /> REFRESH
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-credit-card text-[#94a3b8] text-3xl" />
          </div>
          <p className="text-[#475569] font-syne font-extrabold text-base uppercase tracking-wider mb-1">NO CREDIT REQUESTS</p>
          <p className="text-[#94a3b8] text-[11px] font-bold">Users haven't applied for credit yet</p>
        </div>
      ) : (
        <div className="divide-y-2 divide-[#f1f5f9]">
          {requests.map(req => (
            <div key={req.id} className="p-5 hover:bg-[#f8fafc] transition-all bg-white">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {/* User Info */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25">
                      <span className="text-[11px] font-extrabold text-white">{(req.firstName?.[0]||'')}{(req.lastName?.[0]||'')}</span>
                    </div>
                    <div>
                      <div className="font-syne text-sm font-extrabold text-[#0f172a]">{req.firstName} {req.lastName}</div>
                      <div className="text-[10px] text-[#64748b] font-mono font-bold">{req.email}</div>
                    </div>
                  </div>

                  {/* Reason */}
                  {req.reason && (
                    <div className="text-[11px] text-[#475569] font-bold mb-3 ml-12 bg-[#f8fafc] rounded-lg p-3 border border-[#e2e8f0]">
                      <i className="fas fa-quote-left text-[#94a3b8] mr-1.5 text-[9px]" />{req.reason}
                    </div>
                  )}
                  
                  {/* Document Links */}
                  <div className="flex flex-wrap gap-2.5 ml-12 mb-3">
                    {req.bankStatement ? (
                      <a href={getDocUrl(req.bankStatement)} target="_blank" rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#ecfdf5] border-2 border-[#a7f3d0] text-[#059669] text-[10px] font-mono font-extrabold hover:bg-[#d1fae5] transition-all uppercase tracking-wider">
                        <i className="fas fa-file-invoice text-[10px]" /> BANK STATEMENT
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#94a3b8] text-[10px] font-mono font-extrabold uppercase tracking-wider">
                        <i className="fas fa-times-circle text-[10px]" /> BANK STATEMENT
                      </span>
                    )}
                    {req.panCard ? (
                      <a href={getDocUrl(req.panCard)} target="_blank" rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#ecfdf5] border-2 border-[#a7f3d0] text-[#059669] text-[10px] font-mono font-extrabold hover:bg-[#d1fae5] transition-all uppercase tracking-wider">
                        <i className="fas fa-id-card text-[10px]" /> PAN CARD
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#94a3b8] text-[10px] font-mono font-extrabold uppercase tracking-wider">
                        <i className="fas fa-times-circle text-[10px]" /> PAN CARD
                      </span>
                    )}
                    {req.cibilScreenshot ? (
                      <a href={getDocUrl(req.cibilScreenshot)} target="_blank" rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#ecfdf5] border-2 border-[#a7f3d0] text-[#059669] text-[10px] font-mono font-extrabold hover:bg-[#d1fae5] transition-all uppercase tracking-wider">
                        <i className="fas fa-chart-bar text-[10px]" /> CIBIL SCORE
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#94a3b8] text-[10px] font-mono font-extrabold uppercase tracking-wider">
                        <i className="fas fa-times-circle text-[10px]" /> CIBIL SCORE
                      </span>
                    )}
                  </div>

                  {/* Date */}
                  <div className="text-[9px] text-[#94a3b8] font-mono font-bold ml-12">
                    <i className="fas fa-calendar mr-1.5" />
                    Requested: {new Date(req.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>

                {/* Right Side - Amount & Actions */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-syne text-xl font-extrabold text-[#1e3a5f]">+{fmtUSD(req.amount)}</div>
                    <div className="mt-1.5">{getStatusBadge(req.status)}</div>
                  </div>
                  {req.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(req.id)}
                        disabled={processingIds.includes(req.id)}
                        className="px-4 py-2 rounded-lg bg-[#059669] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all disabled:opacity-50 border-b-[3px] border-[#047857]"
                      >
                        {processingIds.includes(req.id) ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <><i className="fas fa-check mr-1.5" /> APPROVE</>
                        )}
                      </button>
                      <button
                        onClick={() => { setSelectedRequest(req.id); setRejectReason(''); }}
                        disabled={processingIds.includes(req.id)}
                        className="px-4 py-2 rounded-lg bg-[#dc2626] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[3px] border-[#b91c1c]"
                      >
                        <i className="fas fa-times mr-1.5" /> REJECT
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Rejection Reason */}
              {req.rejectionReason && (
                <div className="mt-3 ml-12 p-3 bg-[#fef2f2] border-2 border-[#fecaca] rounded-lg text-[10px] text-[#dc2626] font-mono font-extrabold flex items-center gap-2">
                  <i className="fas fa-exclamation-circle text-[11px]" /> REJECTED: {req.rejectionReason}
                </div>
              )}

              {/* Approval Info */}
              {req.status === 'approved' && req.reviewedAt && (
                <div className="mt-3 ml-12 p-3 bg-[#ecfdf5] border-2 border-[#a7f3d0] rounded-lg text-[10px] text-[#059669] font-mono font-extrabold flex items-center gap-2">
                  <i className="fas fa-check-circle text-[11px]" /> APPROVED on {new Date(req.reviewedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Reason Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedRequest(null)}>
          <div className="relative bg-white border-2 border-[#dc2626] rounded-2xl w-full max-w-[440px] shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b-2 border-[#e2e8f0] bg-[#fef2f2]">
              <h3 className="font-syne text-lg font-extrabold text-[#7f1d1d] uppercase tracking-tight">REJECT CREDIT REQUEST</h3>
              <p className="text-[10px] text-[#991b1b] font-mono font-bold mt-0.5 uppercase tracking-wider">Provide a reason for rejection</p>
            </div>
            <div className="p-6">
              <textarea 
                value={rejectReason} 
                onChange={e => setRejectReason(e.target.value)} 
                placeholder="e.g., Insufficient documentation..." 
                rows={4} 
                className="w-full bg-[#f8fafc] border-2 border-[#cbd5e1] rounded-xl px-4 py-3 text-[#0f172a] font-dm text-sm font-extrabold outline-none focus:border-[#dc2626] focus:bg-white transition-all resize-none placeholder:text-[#94a3b8]" 
              />
              <div className="flex gap-3 mt-5">
                <button 
                  onClick={() => setSelectedRequest(null)} 
                  className="flex-1 py-3 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#f1f5f9] transition-all">
                  CANCEL
                </button>
                <button 
                  onClick={() => { handleReject(selectedRequest); setSelectedRequest(null); }} 
                  disabled={!rejectReason.trim()} 
                  className="flex-1 py-3 rounded-xl bg-[#dc2626] text-white font-syne font-extrabold text-[11px] uppercase tracking-wider hover:bg-[#b91c1c] transition-all disabled:opacity-50 border-b-[3px] border-[#b91c1c]">
                  CONFIRM REJECT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditManagement;